import * as turf from "@turf/turf"
import type { GeoJSON, Polygon } from "geojson"
import { LngLat, LngLatBounds, type LngLatLike } from "mapbox-gl"
import { memo, type PropsWithChildren, useCallback, useMemo } from "react"

import { useAppSelector } from "../../store"
import { selectMapStyle } from "../../store/selectors"
import { mapStyleColorMapping } from "../mappings"
import { type HandlePositionChangeEvent, LayoutFileCornerHandle } from "./LayoutFileCornerHandler"
import { Layer, Source } from "./memo"

export type BoundsChangeEventHandler = (bounds: LngLatBounds) => void

interface EditableMapSectionProps extends PropsWithChildren {
	bounds: LngLatBounds
	onBoundsChange: BoundsChangeEventHandler
	enableEdit: boolean
}

export const EditableMapSection = memo((props: EditableMapSectionProps) => {
	// TODO: (performance) Can be optimized to not calculate things if not enabled.
	const { bounds, enableEdit, children, onBoundsChange } = props
	const mapStyle = useAppSelector(selectMapStyle)
	const mapStyleColor = mapStyleColorMapping[mapStyle]

	const [boundsSouthWest, boundsNorthEast, boundsSouthEast, boundsNorthWest, centerBounds] = useMemo(() => {
		return [
			bounds.getSouthWest(),
			bounds.getNorthEast(),
			bounds.getSouthEast(),
			bounds.getNorthWest(),
			bounds.getCenter(),
		] satisfies [LngLat, LngLat, LngLat, LngLat, LngLat]
	}, [bounds])

	const handleCornerPositionChange = useCallback(
		(e: HandlePositionChangeEvent) => {
			const { position } = e
			let newBounds: LngLatBounds | undefined = undefined

			switch (e.corner) {
				case "center": {
					const offsetLng = position.lng - centerBounds.lng
					const offsetLat = position.lat - centerBounds.lat

					newBounds = new LngLatBounds(
						[boundsSouthWest.lng + offsetLng, boundsSouthWest.lat + offsetLat],
						[boundsNorthEast.lng + offsetLng, boundsNorthEast.lat + offsetLat],
					)
					break
				}
				case "northEast": {
					newBounds = new LngLatBounds(boundsSouthWest, position)
					break
				}
				case "southWest": {
					newBounds = new LngLatBounds(position, boundsNorthEast)
					break
				}
				case "northWest": {
					const newSouthWest: LngLatLike = [position.lng, boundsSouthWest.lat]
					const newNorthEast: LngLatLike = [boundsNorthEast.lng, position.lat]
					newBounds = new LngLatBounds(newSouthWest, newNorthEast)
					break
				}
				case "southEast": {
					const newSouthWest: LngLatLike = [boundsSouthWest.lng, position.lat]
					const newNorthEast: LngLatLike = [position.lng, boundsNorthEast.lat]
					newBounds = new LngLatBounds(newSouthWest, newNorthEast)
					break
				}
			}

			onBoundsChange(newBounds)
		},
		[boundsNorthEast, boundsSouthWest, centerBounds, onBoundsChange],
	)

	const polygon = useMemo<GeoJSON<Polygon>>(() => {
		return turf.bboxPolygon(turf.bbox(turf.multiPoint(bounds.toArray())))
	}, [bounds])

	const lineLayerPaint = useMemo(
		() => ({
			"line-color": mapStyleColor,
			"line-dasharray": [5, 5],
			"line-width": 1.5,
		}),
		[mapStyleColor],
	)

	return (
		<>
			{enableEdit && (
				<Source id="editable-map-section-source" type="geojson" data={polygon}>
					<Layer id="editable-map-section-line" type="line" paint={lineLayerPaint} />
				</Source>
			)}
			{children}
			{enableEdit && (
				<>
					<LayoutFileCornerHandle
						corner="northWest"
						position={boundsNorthWest}
						onPositionChange={handleCornerPositionChange}
					/>
					<LayoutFileCornerHandle
						corner="northEast"
						position={boundsNorthEast}
						onPositionChange={handleCornerPositionChange}
					/>
					<LayoutFileCornerHandle
						corner="southWest"
						position={boundsSouthWest}
						onPositionChange={handleCornerPositionChange}
					/>
					<LayoutFileCornerHandle
						corner="southEast"
						position={boundsSouthEast}
						onPositionChange={handleCornerPositionChange}
					/>
					<LayoutFileCornerHandle
						corner="center"
						position={centerBounds}
						onPositionChange={handleCornerPositionChange}
					/>
				</>
			)}
		</>
	)
})
EditableMapSection.displayName = "EditableMapSection"
